import React, { useState, useEffect, useCallback } from 'react';
import { PlusCircle, Edit3, Trash2 } from 'lucide-react';
import { Modal, ConfirmationModal } from './Helpers';
import { fetchPeople } from '../api/personApi'; // fetchPeople'ı import et

const PeopleManagementView = ({ showToast, currentUser }) => {
  const [people, setPeople] = useState([]);
  const [personName, setPersonName] = useState('');
  const [personContact, setPersonContact] = useState('');
  const [personRole, setPersonRole] = useState('sakin'); // Varsayılan rol
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState({ isOpen: false, personId: null });
  const [isLoading, setIsLoading] = useState(false); // Veri yükleme ve işlem için genel

  const loadPeople = useCallback(async () => {
    if (!currentUser?.buildingId) {
      showToast('Kullanıcıya atanmış bina bulunamadı.', 'error');
      setPeople([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const data = await fetchPeople(currentUser.buildingId);
      setPeople(data || []);
    } catch (error) {
      showToast("Kişiler yüklenirken hata: " + error.message, "error");
      setPeople([]);
    } finally {
      setIsLoading(false);
    }
  }, [showToast, currentUser]);

  useEffect(() => {
    loadPeople();
  }, [loadPeople]);

  const handleAddOrUpdatePerson = async (e) => {
    e.preventDefault();
    if (!personName.trim()) {
      showToast('Kişi adı boş olamaz.', 'error');
      return;
    }
    if (!personRole) {
      showToast('Lütfen bir rol seçin.', 'error');
      return;
    }

    const personData = { 
      name: personName.trim(), 
      contact: personContact.trim() || null, // Boşsa null gönder
      role: personRole 
    };
    setIsLoading(true);
    try {
      if (editingPerson) {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/people/${editingPerson.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(personData),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Kişi güncellenemedi.');
        }
        showToast('Kişi başarıyla güncellendi.', 'success');
      } else {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/people`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(personData),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Kişi eklenemedi.');
        }
        showToast('Kişi başarıyla eklendi.', 'success');
      }
      loadPeople();
      closeModal();
    } catch (error) {
      showToast('Kişi kaydedilirken hata: ' + (error.message || "Bilinmeyen bir hata."), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const openModalForEdit = (person) => {
    setEditingPerson(person);
    setPersonName(person.name);
    setPersonContact(person.contact || '');
    setPersonRole(person.role || 'sakin');
    setIsModalOpen(true);
  };

  const openModalForNew = () => {
    setEditingPerson(null);
    setPersonName('');
    setPersonContact('');
    setPersonRole('sakin');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingPerson(null);
    setPersonName('');
    setPersonContact('');
    setPersonRole('sakin');
  };

  const handleDeletePerson = async (personId) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/people/${personId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Kişi silinemedi.');
      }
      showToast('Kişi başarıyla silindi.', 'success');
      loadPeople();
    } catch (error) {
      const errorMessage = error.message || 'Kişi silinirken bir hata oluştu.';
      if (error.message && (error.message.includes("atanmıştır") || error.message.includes("ilişkili"))) {
        showToast(errorMessage, 'warning', 6000);
      } else {
        showToast(errorMessage, 'error');
      }
    } finally {
      setIsLoading(false);
      setConfirmDeleteModal({ isOpen: false, personId: null });
    }
  };

  const getRoleDisplayName = (role) => {
    switch(role) {
      case 'sakin': return 'Sakin';
      case 'katmaliki': return 'Kat Maliki';
      case 'hemSakinHemKatMaliki': return 'Sakin ve Kat Maliki';
      default: return role || 'Belirtilmemiş';
    }
  };

  // Tarih formatlama fonksiyonu (Invalid Date sorununu çözmek için)
  const formatDate = (dateString) => {
    if (!dateString) return '-'; // Eğer tarih yoksa '-' döndür
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-'; // Geçersiz tarihse '-' döndür
    return date.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow relative">
      {isLoading && !isModalOpen && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-30">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <p className="ml-3 text-gray-700">Kişiler yükleniyor...</p>
        </div>
      )}
      <ConfirmationModal 
        isOpen={confirmDeleteModal.isOpen} 
        onClose={() => setConfirmDeleteModal({ isOpen: false, personId: null })} 
        title="Kişiyi Sil" 
        message="Bu kişiyi silmek istediğinizden emin misiniz? Eğer bu kişi dairelere atanmışsa veya aidat borçları varsa silme işlemi başarısız olabilir." 
        onConfirm={() => handleDeletePerson(confirmDeleteModal.personId)}
      />
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Kişi Yönetimi</h1>
        <button 
          onClick={openModalForNew} 
          className="btn-primary-style flex items-center disabled:opacity-50"
          disabled={isLoading}
        >
          <PlusCircle size={18} className="mr-2"/>Yeni Kişi Ekle
        </button>
      </div>
      <Modal isOpen={isModalOpen} onClose={closeModal} title={editingPerson ? "Kişiyi Düzenle" : "Yeni Kişi Ekle"}>
        <form onSubmit={handleAddOrUpdatePerson}>
          <div className="mb-4">
            <label htmlFor="pNameModalPeople" className="label-form-style">Ad Soyad:</label>
            <input 
              type="text" 
              id="pNameModalPeople" 
              value={personName} 
              onChange={e => setPersonName(e.target.value)} 
              className="input-form-style" 
              required 
              disabled={isLoading && isModalOpen}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="pContactModalPeople" className="label-form-style">İletişim (Tel/E-posta, Opsiyonel):</label>
            <input 
              type="text" 
              id="pContactModalPeople" 
              value={personContact} 
              onChange={e => setPersonContact(e.target.value)} 
              className="input-form-style" 
              disabled={isLoading && isModalOpen}
            />
          </div>
          <div className="mb-6">
            <label htmlFor="pRoleModalPeople" className="label-form-style">Rol:</label>
            <select 
              id="pRoleModalPeople" 
              value={personRole} 
              onChange={e => setPersonRole(e.target.value)} 
              className="input-form-style" 
              disabled={isLoading && isModalOpen}
            >
              <option value="sakin">Sakin</option>
              <option value="katmaliki">Kat Maliki</option>
              <option value="hemSakinHemKatMaliki">Hem Sakin Hem Kat Maliki</option>
            </select>
          </div>
          <div className="flex justify-end space-x-3">
            <button 
              type="button" 
              onClick={closeModal} 
              className="btn-secondary-style" 
              disabled={isLoading && isModalOpen}
            >
              İptal
            </button>
            <button 
              type="submit" 
              className="btn-primary-style flex items-center disabled:opacity-50" 
              disabled={isLoading && isModalOpen}
            >
              {isLoading && isModalOpen && <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>}
              {editingPerson ? "Güncelle" : "Kaydet"}
            </button>
          </div>
        </form>
      </Modal>
      {!isLoading && people.length === 0 ? <p className="text-gray-500">Henüz kişi eklenmemiş.</p> : null}
      {!isLoading && people.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="th-style">Ad Soyad</th>
                <th className="th-style">İletişim</th>
                <th className="th-style">Rol</th>
                
                <th className="th-style">İşlemler</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {people.map(p => (
                <tr key={p.id}>
                  <td className="td-style font-medium">{p.name}</td>
                  <td className="td-style">{p.contact || '-'}</td>
                  <td className="td-style">{getRoleDisplayName(p.role)}</td>
                  <td className="td-style space-x-2">
                    <button 
                      onClick={() => openModalForEdit(p)} 
                      className="icon-button-indigo" 
                      title="Düzenle" 
                      disabled={isLoading}
                    >
                      <Edit3 size={18}/>
                    </button>
                    <button 
                      onClick={() => setConfirmDeleteModal({ isOpen: true, personId: p.id })} 
                      className="icon-button-red" 
                      title="Sil" 
                      disabled={isLoading}
                    >
                      <Trash2 size={18}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <style jsx>{`
        .th-style { padding: 0.75rem 1.5rem; text-align: left; font-size: 0.75rem; font-weight: 500; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; }
        .td-style { padding: 0.75rem 1.5rem; white-space: nowrap; font-size: 0.875rem; color: #374151; }
        .label-form-style { display: block; margin-bottom: 0.25rem; font-size: 0.875rem; font-weight: 500; color: #374151; }
        .input-form-style { width: 100%; padding: 0.5rem; border: 1px solid #D1D5DB; border-radius: 0.375rem; box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); }
        .btn-primary-style { padding: 0.5rem 1rem; font-size: 0.875rem; color: white; background-color: #2563EB; border-radius: 0.375rem; } 
        .btn-primary-style:hover { background-color: #1D4ED8; }
        .btn-secondary-style { padding: 0.5rem 1rem; font-size: 0.875rem; color: #374151; background-color: #E5E7EB; border-radius: 0.375rem; } 
        .btn-secondary-style:hover { background-color: #D1D5DB; }
        .icon-button-indigo { color: #4f46e5; } 
        .icon-button-indigo:hover { color: #3730a3; }
        .icon-button-red { color: #dc2626; } 
        .icon-button-red:hover { color: #b91c1c; }
      `}</style>
    </div>
  );
};

export default PeopleManagementView;